import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { UserService } from '../user/user.service';
import { AuthApiService } from './auth.api.service';
import { IUserDetails, IUserInfoDTO } from '@bashaquerizwan/azaadi-packages';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { USER_INFO_LOCAL_STORAGE_KEY } from '@shared/utils/constant';
import { IAccess } from '@shared/models';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _authApiService = inject(AuthApiService);
  private readonly _userService = inject(UserService);
  private readonly _user = signal<IUserDetails | null>(null);
  private readonly _router = inject(Router);

  logout(): void {
    localStorage.removeItem(USER_INFO_LOCAL_STORAGE_KEY);
    this._userService.setLoggedInUser(null);
    this.sendToLogout();
  }
  public getCurrentUser(): Observable<any> {
    let userData: any = {} as any;
    const usrStr = localStorage.getItem('user');
    if (usrStr) userData = JSON.parse(usrStr);
    return of(userData);
  }

  getLoggedInUserInfo(): any {
    const userStr = localStorage.getItem(USER_INFO_LOCAL_STORAGE_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  loadUserDetails(token: string): Observable<any> {
    return this._authApiService.getUserByToken(token);
  }

  setCurrentUser(user: any): void {
    this._user.set(user);
    localStorage.setItem(USER_INFO_LOCAL_STORAGE_KEY, JSON.stringify(user));
  }

  getCurrentUserInfo() {
    return this._user.asReadonly();
  }

  sendToAuth() {
    localStorage.removeItem(USER_INFO_LOCAL_STORAGE_KEY);
    const url = `${environment.authUrl}?redirect-url=${document.location.origin}`;
    document.location.href = url;
  }

  sendToLogout() {
    const url = `${environment.authUrl}/logout?redirect-url=${document.location.origin}`;
    document.location.href = url;
  }

  getLocalRedirectUrl(): string | null {
    return localStorage.getItem('redirectUrl') || null;
  }

  setLocalRedirectUrl(url: string) {
    localStorage.setItem('redirectUrl', url == '/auth' ? '/home' : url);
  }
  removeLocalRedirectUrl() {
    localStorage.removeItem('redirectUrl');
  }

  /**
   * Checks if the current user has access to a specific tag.
   * @param tag The access tag to check.
   * @returns True if the user has view or write access for the tag, or has universal access.
   */
  hasAccess(tag: string): boolean {
    try {
      const userInfo = this.getLoggedInUserInfo();
      const accesses: IAccess[] = userInfo?.tUserAccess ?? [];

      // Check for universal access first
      if (this.hasUniversalAccess()) {
        return true;
      }

      // Check for specific tag access
      return accesses.some((access) => {
        if (access.sTag === tag) {
          return access.bCanView || access.bCanWrite;
        }
        // Fallback to checking by sName if sTag is missing or doesn't match directly
        if (access.sName) {
          // Extract expected name from tag (e.g. atTagHrPolicyAccess -> HrPolicy)
          const expectedName = tag.replace(/^at(?:Tag)?/, '').replace(/Access$/, '');
          if (expectedName.toLowerCase() === access.sName.toLowerCase()) {
            return access.bCanView || access.bCanWrite;
          }
        }
        return false;
      });
    } catch {
      return false;
    }
  }

  /**
   * Checks if the current user has universal access (all access).
   * @returns True if the user has universal access.
   */
  hasUniversalAccess(): boolean {
    try {
      const userInfo = this.getLoggedInUserInfo();
      const accesses: IAccess[] = userInfo?.tUserAccess ?? [];
      return accesses.some((access) => {
        if (access.sTag === 'atTagUniversalAccess') return access.bCanView || access.bCanWrite;
        if (access.sName && access.sName.toLowerCase() === 'universal') return access.bCanView || access.bCanWrite;
        return false;
      });
    } catch {
      return false;
    }
  }

  /**
   * Checks if the current user has access to any of the provided tags.
   * @param tags Array of access tags to check.
   * @returns True if the user has access to at least one of the tags.
   */
  hasAnyAccess(tags: string[]): boolean {
    if (this.hasUniversalAccess()) {
      return true;
    }
    return tags.some((tag) => this.hasAccess(tag));
  }
}
